import React, { useEffect, useState } from "react";

import { adminTicketsService } from "api/services/tickets.service";
import TicketRefundModal from "components/ticket-group/TicketRefundModal";
import Badge from "components/UI/Badge";
import Button from "components/UI/Button";
import { Input, Select } from "components/UI/Input";
import Modal from "components/UI/Modal";
import { getApiErrorMessage } from "utils/api-error";
import { TICKET_STATUS_BADGE, TICKET_STATUS_OPTIONS } from "utils/constant/ticket";

import type { AdminTicketDetail, AdminTicketUpdateRequest, TicketStatus } from "api/types/tickets.types";

import "./TicketPurchaseDetailModal.scss";

interface TicketEditForm {
  sessions_total: string;
  sessions_used: string;
  status: TicketStatus;
  start_date: string;
  valid_until: string;
}

const buildForm = (t: AdminTicketDetail | null): TicketEditForm => ({
  sessions_total: t ? String(t.sessions_total) : "",
  sessions_used: t ? String(t.sessions_used) : "",
  status: t?.status ?? "UNUSED",
  start_date: t?.start_date ?? "",
  valid_until: t?.valid_until ?? "",
});

const formatDateTime = (v: string | null): string => (v ? v.slice(0, 16).replace("T", " ") : "-");
const formatDate = (v: string | null): string => (v ? v.slice(0, 10) : "-");
const formatTime = (v: string): string => (v ? v.slice(0, 5) : "-");
const formatAmount = (v: string | null): string => (v != null && v !== "" ? `${Number(v).toLocaleString()}원` : "-");

interface TicketPurchaseDetailModalProps {
  open: boolean;
  ticketId: number | null;
  onClose: () => void;
  onUpdated?: () => void;
  onDeleted?: () => void;
}

const TicketPurchaseDetailModal: React.FC<TicketPurchaseDetailModalProps> = ({ open, ticketId, onClose, onUpdated, onDeleted }) => {
  const [detail, setDetail] = useState<AdminTicketDetail | null>(null);
  const [form, setForm] = useState<TicketEditForm>(buildForm(null));
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [refundOpen, setRefundOpen] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || ticketId == null) return;
    let cancelled = false;
    setLoading(true);
    setError("");
    setDetail(null);
    adminTicketsService
      .get(ticketId)
      .then((res) => {
        if (cancelled) return;
        setDetail(res.data);
        setForm(buildForm(res.data));
      })
      .catch((err) => {
        if (!cancelled) setError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, ticketId]);

  const handleSave = async () => {
    if (!detail) return;
    const total = Number(form.sessions_total);
    const used = Number(form.sessions_used);
    if (!Number.isInteger(total) || total < 0) {
      setError("전체 세션 수는 0 이상의 정수여야 합니다.");
      return;
    }
    if (!Number.isInteger(used) || used < 0) {
      setError("사용 세션 수는 0 이상의 정수여야 합니다.");
      return;
    }
    if (used > total) {
      setError("사용 세션 수는 전체 세션 수를 초과할 수 없습니다.");
      return;
    }

    const payload: AdminTicketUpdateRequest = {
      sessions_total: total,
      sessions_used: used,
      status: form.status,
      start_date: form.start_date || null,
      valid_until: form.valid_until || null,
    };

    setSaving(true);
    setError("");
    try {
      const res = await adminTicketsService.update(detail.id, payload);
      setDetail(res.data);
      setForm(buildForm(res.data));
      onUpdated?.();
    } catch (err) {
      setError(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // const handleDelete = async () => {
  //   if (!detail) return;
  //   if (!window.confirm("이 수강권을 삭제하시겠습니까? 삭제 후에는 복구할 수 없습니다.")) return;
  //   setDeleting(true);
  //   setError("");
  //   try {
  //     await adminTicketsService.remove(detail.id);
  //     onDeleted?.();
  //     onClose();
  //   } catch (err) {
  //     setError(getApiErrorMessage(err));
  //   } finally {
  //     setDeleting(false);
  //   }
  // };

  const handleRefunded = () => {
    onDeleted?.();
    onClose();
  };

  // 전체 환불만 지원 — 미사용이거나 예약만 하고 아직 한 회차도 사용하지 않은 경우에만 환불 가능
  const canRefund = !!detail && detail.order_id != null && detail.sessions_used === 0 && detail.status !== "CANCELLED";

  const busy = saving;

  return (
    <>
      <Modal
        open={open}
        title={detail ? `수강권 상세 #${detail.id}` : "수강권 상세"}
        width="760px"
        onClose={busy ? undefined : onClose}
        closeOnOverlayClick={!busy}
        footer={
          detail ? (
            <div className="ticket-detail__footer">
              <Button variant="danger" onClick={() => setRefundOpen(true)} disabled={busy || !canRefund} title={canRefund ? undefined : "전체 환불은 수강권을 한 회차도 사용하지 않은 경우에만 가능합니다."}>
                환불
              </Button>
              <div className="ticket-detail__footer-right">
                <Button variant="primary" onClick={handleSave} disabled={busy}>
                  {saving ? "저장 중..." : "변경 저장"}
                </Button>
              </div>
            </div>
          ) : undefined
        }
      >
        {loading ? (
          <div className="ticket-detail__loading">불러오는 중...</div>
        ) : !detail ? (
          <div className="ticket-detail__error">{error || "데이터를 불러올 수 없습니다."}</div>
        ) : (
          <div className="ticket-detail">
            {error && <div className="ticket-detail__error">{error}</div>}

            {/* 구매/회원 정보 (읽기 전용) */}
            <section className="ticket-detail__section">
              <h4 className="ticket-detail__section-title">구매 정보</h4>
              <dl className="ticket-detail__grid">
                <div>
                  <dt>회원 ID</dt>
                  <dd>{detail.user_id}</dd>
                </div>
                <div>
                  <dt>회원명</dt>
                  <dd>{detail.user_name}</dd>
                </div>
                <div>
                  <dt>강습 상품</dt>
                  <dd>{detail.lesson_product_name}</dd>
                </div>
                <div>
                  <dt>주문번호</dt>
                  <dd>{detail.order_number ?? "-"}</dd>
                </div>
                <div>
                  <dt>결제 금액</dt>
                  <dd>{formatAmount(detail.payment_amount)}</dd>
                </div>
                <div>
                  <dt>결제 일시</dt>
                  <dd>{formatDateTime(detail.payment_date)}</dd>
                </div>
                <div>
                  <dt>상태</dt>
                  <dd>
                    <Badge variant={TICKET_STATUS_BADGE[detail.status] ?? "draft"}>{detail.status_display}</Badge>
                  </dd>
                </div>
                <div>
                  <dt>활성</dt>
                  <dd>{detail.is_active ? <Badge variant="active">활성</Badge> : <Badge variant="draft">비활성</Badge>}</dd>
                </div>
                <div>
                  <dt>생성일</dt>
                  <dd>{formatDateTime(detail.created_at)}</dd>
                </div>
                <div>
                  <dt>수정일</dt>
                  <dd>{formatDateTime(detail.updated_at)}</dd>
                </div>
              </dl>
            </section>

            {/* 수정 가능 영역 */}
            <section className="ticket-detail__section">
              <h4 className="ticket-detail__section-title">수강권 수정</h4>
              <div className="ticket-detail__form">
                <Input label="전체 세션 수" type="number" min={0} value={form.sessions_total} onChange={(e) => setForm((p) => ({ ...p, sessions_total: e.target.value }))} />
                <Input label="사용 세션 수" type="number" min={0} value={form.sessions_used} onChange={(e) => setForm((p) => ({ ...p, sessions_used: e.target.value }))} />
                <Select label="상태" value={form.status} onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as TicketStatus }))} options={TICKET_STATUS_OPTIONS} />
                <Input label="시작일" type="date" value={form.start_date} onChange={(e) => setForm((p) => ({ ...p, start_date: e.target.value }))} />
                <Input label="만료일" type="date" value={form.valid_until} onChange={(e) => setForm((p) => ({ ...p, valid_until: e.target.value }))} />
              </div>
              <p className="ticket-detail__notice">※ 활성(active) 여부는 세션 사용량·만료일에 따라 자동으로 보정되므로 직접 변경할 수 없습니다.</p>
            </section>

            {/* 예약(사용) 내역 */}
            <section className="ticket-detail__section">
              <h4 className="ticket-detail__section-title">예약 내역 ({detail.reservations.length})</h4>
              {detail.reservations.length === 0 ? (
                <p className="ticket-detail__empty">예약 내역이 없습니다.</p>
              ) : (
                <div className="ticket-detail__table-wrap">
                  <table className="ticket-detail__table">
                    <thead>
                      <tr>
                        <th>날짜</th>
                        <th>시간</th>
                        <th>강사</th>
                        <th>장소</th>
                        <th>구분</th>
                        <th>차감</th>
                        <th>상태</th>
                      </tr>
                    </thead>
                    <tbody>
                      {detail.reservations.map((r) => (
                        <tr key={r.id}>
                          <td>{formatDate(r.date)}</td>
                          <td>
                            {formatTime(r.start_time)}~{formatTime(r.end_time)}
                          </td>
                          <td>{r.instructor_name || "-"}</td>
                          <td>{r.location_name || "-"}</td>
                          <td>{r.is_theory ? "이론" : r.day_order != null ? `Day ${r.day_order}` : "실습"}</td>
                          <td>{r.usage_count_used}</td>
                          <td>{r.status_display}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </section>
          </div>
        )}
      </Modal>

      <TicketRefundModal open={refundOpen} ticket={detail} onClose={() => setRefundOpen(false)} onRefunded={handleRefunded} />
    </>
  );
};

export default TicketPurchaseDetailModal;
